import { ErrorCode } from '@errorHandler/constants';
import { FatalError } from '@errorHandler/errors';
import { DEFAULT_DPA_DOCUMENT_NAME } from '@/setup/containers/data-processing-addendum/DataProcessingAddendumPage';
import { CommonHeaders, Endpoints, StorageKeys, SystemType } from './constants';
import type {
  CheckDpaConsentRequest,
  CheckDpaConsentResponse,
  ClientCreateRequest,
  ClientCreateResponse,
  DomainsValidateRequest,
  FileAccessModifier,
  GetAadConsentRequest,
  GetAadConsentResponse,
  IdpConfigSaveRequest,
  IdpConfigSaveResponse,
  IdpConfigsFindRequest,
  IdpConfigsFindResponse,
  IdpMetadataSaveRequest,
  OrgInfoGetResponse,
  OrgInfoSaveRequest,
  PrepareFileUploadResponse,
  TenantSetupCompleteRequest,
  MetadataSaveResponse,
} from './types';
import { fetcher } from './utils';
import type { DomSerialized } from '@imprivata-cloud/components';

function icpMetadataSave(data: IdpMetadataSaveRequest, isSetup = false): Promise<MetadataSaveResponse> {
  return fetcher(Endpoints.IDP_SAVE, {
    method: 'POST',
    body: JSON.stringify(data),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
    systemType: isSetup ? SystemType.SETUP : SystemType.PORTAL,
  });
}

export function orgInfoGet(): Promise<OrgInfoGetResponse> {
  return fetcher(Endpoints.ORG_INFO_GET, { method: 'POST' });
}

function saveOrgInfo(data: OrgInfoSaveRequest) {
  return fetcher(Endpoints.ORG_INFO_SAVE, {
    method: 'POST',
    body: JSON.stringify(data),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
  });
}

async function saveIdpConfigSAML(data: IdpConfigSaveRequest, isSetup = false): Promise<IdpConfigSaveResponse> {
  return fetcher(Endpoints.SAVE_AND_UPDATE_IDP_CONFIG_SAML, {
    method: 'POST',
    body: JSON.stringify(data),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
    systemType: isSetup ? SystemType.SETUP : SystemType.PORTAL,
  });
}

const getIpRanges = async (): Promise<string[]> => {
  return ['44.207.16.175/32', '44.196.189.191/32', '34.196.47.118/32'];
};

const getAadConsentUrl = async (data: GetAadConsentRequest): Promise<GetAadConsentResponse> => {
  return fetcher(Endpoints.AAD_CONSENT_URL, {
    method: 'POST',
    body: JSON.stringify(data),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
  });
};

function createTenantSetupCompleteRequest(withEntraId: boolean): TenantSetupCompleteRequest {
  const email = localStorage.getItem(StorageKeys.ORG_EMAIL);

  if (!email) {
    console.log('Email not found in localStorage');
    throw new FatalError({ code: ErrorCode.FATAL_ERROR, message: 'Email not found in localStorage' });
  }

  return {
    email,
    withEntraId: withEntraId,
    consent: {
      grantType: sessionStorage.getItem(StorageKeys.DPA_STATUS) || undefined,
      documentName: sessionStorage.getItem(StorageKeys.DPA_DOCUMENT_NAME) || DEFAULT_DPA_DOCUMENT_NAME,
    },
  };
}

const sendTenantSetupCompleteRequest = async (withEntraId: boolean): Promise<boolean> => {
  const request = createTenantSetupCompleteRequest(withEntraId);
  return fetcher(Endpoints.COMPLETE_SETUP, {
    method: 'POST',
    body: JSON.stringify(request),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
  });
};

const startTenantSetup = async (bearerToken: string | null) => {
  if (!bearerToken) {
    throw new Error('Bearer token is missing');
  }
  return fetcher(Endpoints.START_TENANT_SETUP, {
    method: 'POST',
    body: JSON.stringify({ bearerToken: bearerToken }),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
  });
};

export const setupLinkRegenerate = async (bearerToken: string | null): Promise<void> => {
  if (!bearerToken) {
    throw new Error('Bearer token is missing');
  }
  return fetcher(Endpoints.SETUP_LINK_REGENERATE, {
    method: 'POST',
    body: JSON.stringify({ bearerToken: bearerToken }),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
  });
};

async function dpaCheck(): Promise<CheckDpaConsentResponse> {
  return fetcher(Endpoints.DPA_CHECK, {
    method: 'POST',
    body: JSON.stringify({ consentDocumentName: DEFAULT_DPA_DOCUMENT_NAME } as CheckDpaConsentRequest),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
  });
}

export async function getConsentDocument(url: string): Promise<DomSerialized> {
  const resp = await fetch(url, { method: 'GET', credentials: 'omit' });
  return resp.json();
}

async function createToken(clientCreateRequest: ClientCreateRequest, isSetup: boolean): Promise<ClientCreateResponse> {
  return fetcher(Endpoints.CONNECT_CLIENT_CREATE, {
    method: 'POST',
    body: JSON.stringify(clientCreateRequest),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
    systemType: isSetup ? SystemType.SETUP : SystemType.PORTAL,
  });
}

async function getUploadUrl(
  fileName: string,
  accessModifier: FileAccessModifier,
  systemType?: SystemType,
): Promise<PrepareFileUploadResponse> {
  return fetcher(Endpoints.PREPARE_FILE_UPLOAD, {
    method: 'POST',
    body: JSON.stringify({ fileSuffix: fileName, fileAccessModifier: accessModifier }),
    workflowId: sessionStorage.getItem(StorageKeys.WORKFLOW_ID),
    systemType,
  });
}

const uploadFile = async (file: File, uploadUrl: string) => {
  const fileArray = await file.arrayBuffer();
  return fetch(uploadUrl, {
    method: 'PUT',
    body: fileArray,
    headers: {
      ...CommonHeaders,
      'Content-Type': 'application/octet-stream',
    },
  })
    .then(async (response) => {
      if (response.ok) {
        return response
          .json()
          .then((json) => json)
          .catch(() => null); //response with empty body
      }
      const error = await response.json().then((json) => json);
      return Promise.reject(error.error);
    })
    .catch((error) => {
      console.error('Error fetching data:', error);
      return Promise.reject(error);
    });
};

const validateDomain = async (domains: DomainsValidateRequest): Promise<void> => {
  return fetcher(Endpoints.DOMAINS_VALIDATE, {
    method: 'POST',
    body: JSON.stringify(domains),
  });
};

async function findIdpConfigSAML(data: IdpConfigsFindRequest, tenantId: string): Promise<IdpConfigsFindResponse> {
  return fetcher(
    Endpoints.IDP_CONFIG_SAML_FIND,
    {
      method: 'POST',
      body: JSON.stringify(data),
    },
    tenantId,
  );
}

export {
  icpMetadataSave,
  getIpRanges,
  saveOrgInfo,
  getAadConsentUrl,
  saveIdpConfigSAML,
  startTenantSetup,
  createTenantSetupCompleteRequest,
  sendTenantSetupCompleteRequest,
  getUploadUrl,
  uploadFile,
  dpaCheck,
  createToken,
  validateDomain,
  findIdpConfigSAML,
};
